import type {RefObject} from 'react'
import {useIsRouteMounted} from '@github-ui/react-core/future/use-is-route-mounted'
import {useChildRouteQuery} from '@github-ui/react-core/future/use-route-query'
import type {AnyQueryRoute} from '@github-ui/react-core/future/query-route'
import {
  pullRequestsChangesRoute,
  pullRequestsChangesWithRangeRoute,
  pullRequestsFilesRoute,
  pullRequestsFilesWithRangeRoute,
} from '../routes/files-changed-routes'
import {buildNormalizedMarkers, buildPendingReview} from '../helpers/diff-helpers'
import {useLayoutStoreInstance} from '../stores/LayoutStore'
import type {FilesRoutePayload} from '../page-data/payloads/files'

/**
 * Populates a ref with the files-changed child route payload. Must render as a
 * sibling **before** LayoutStoreProvider so the ref is populated when the
 * provider reads it during store creation.
 *
 * Uses the two-component pattern: an outer component checks `useIsRouteMounted`
 * (safe, never throws) and only renders the inner component when the route is active.
 *
 * Renders null — this is a side-effect-only component.
 */
export function FilesChangedDataCapture({dataRef}: {dataRef: RefObject<FilesRoutePayload | undefined>}) {
  return (
    <>
      <RouteDataCapture route={pullRequestsChangesRoute} dataRef={dataRef} />
      <RouteDataCapture route={pullRequestsChangesWithRangeRoute} dataRef={dataRef} />
      <RouteDataCapture route={pullRequestsFilesRoute} dataRef={dataRef} />
      <RouteDataCapture route={pullRequestsFilesWithRangeRoute} dataRef={dataRef} />
    </>
  )
}

type FilesChangedRoute = AnyQueryRoute

function RouteDataCapture({
  route,
  dataRef,
}: {
  route: FilesChangedRoute
  dataRef: RefObject<FilesRoutePayload | undefined>
}) {
  const isMounted = useIsRouteMounted(route)
  if (!isMounted) return null
  return <RouteDataCaptureInner route={route} dataRef={dataRef} />
}

function RouteDataCaptureInner({
  route,
  dataRef,
}: {
  route: FilesChangedRoute
  dataRef: RefObject<FilesRoutePayload | undefined>
}) {
  const {data} = useChildRouteQuery(route, 'mainQuery') as {data: FilesRoutePayload | undefined}
  // Populate the ref during render so LayoutStoreProvider (a later sibling) can
  // read it during store creation. React renders siblings in order.
  // eslint-disable-next-line react-hooks/refs
  if (data) dataRef.current = data
  return null
}

/**
 * Seeds the LayoutStore with markers and pendingReview from the files-changed
 * child route on subsequent soft-navigations (after initial store creation).
 * The initial SSR seed is handled by FilesChangedDataCapture + LayoutStoreProvider.
 *
 * Renders null — this is a side-effect-only component.
 */
export function LayoutStoreSeeder({seededRef}: {seededRef: RefObject<boolean>}) {
  return (
    <>
      <RouteSeeder route={pullRequestsChangesRoute} seededRef={seededRef} />
      <RouteSeeder route={pullRequestsChangesWithRangeRoute} seededRef={seededRef} />
      <RouteSeeder route={pullRequestsFilesRoute} seededRef={seededRef} />
      <RouteSeeder route={pullRequestsFilesWithRangeRoute} seededRef={seededRef} />
    </>
  )
}

function RouteSeeder({route, seededRef}: {route: FilesChangedRoute; seededRef: RefObject<boolean>}) {
  const isMounted = useIsRouteMounted(route)
  if (!isMounted) return null
  return <RouteSeederInner route={route} seededRef={seededRef} />
}

function RouteSeederInner({route, seededRef}: {route: FilesChangedRoute; seededRef: RefObject<boolean>}) {
  const {data} = useChildRouteQuery(route, 'mainQuery') as {data: FilesRoutePayload | undefined}
  const store = useLayoutStoreInstance()

  // Intentionally accessing the ref during render for synchronous seeding.
  // This handles soft-nav to files-changed after the store was already created
  // with empty data (e.g. user navigated from conversation to files-changed).
  // eslint-disable-next-line react-hooks/refs
  if (data && !seededRef.current) {
    seededRef.current = true
    const normalizedMarkers = buildNormalizedMarkers(data.markers, data.diffSummaries)
    if (normalizedMarkers) {
      store.getState().layoutStoreActions.hydrateMarkers(normalizedMarkers)
    }
    store.getState().layoutStoreActions.hydratePendingReview(buildPendingReview(data.viewerPendingReview))
    store.setState({layoutStoreSeeded: true})
  }

  return null
}
